import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
  getOrganizationUsersOrganizationOrganizationIdUsersGetQueryKey,
  importOrganizationUsersOrganizationOrganizationIdUsersImportPostMutation,
} from '@/generated/@tanstack/react-query.gen'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { DialogClose, DialogTrigger } from '@radix-ui/react-dialog'
import template from '@/assets/files/hydra-import-template.xlsx'
import ErrorMessages from './import-message-error'
import { useUser } from '@/pages/enterprise/user-provider'
import { AxiosError } from 'axios'
import { HTTPValidationError } from '@/generated/types.gen'

export function ImportDataDialog() {
  const { onUpdateSeats, organizationId, availableSeats } = useUser()
  const [fileName, setFileName] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [open, setOpen] = useState(false)
  const [errors, setErrors] = useState<AxiosError<
    HTTPValidationError,
    any
  > | null>(null)

  const disabled = availableSeats === 0

  const queryClient = useQueryClient()

  const { mutate, isPending } = useMutation({
    ...importOrganizationUsersOrganizationOrganizationIdUsersImportPostMutation(),
    onSuccess: () => {
      setSuccess(true)
      queryClient.invalidateQueries({
        queryKey:
          getOrganizationUsersOrganizationOrganizationIdUsersGetQueryKey({
            path: {
              organization_id: organizationId,
            },
          }),
      })
      onUpdateSeats()
    },
    onError: (error) => {
      setErrors(error)
    },
  })

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      setFileName(file.name)
      mutate({
        path: {
          organization_id: organizationId,
        },
        body: { file: file },
      })
    }
  }

  useEffect(() => {
    if (!open) {
      setFileName(null)
      setErrors(null)
      setSuccess(false)
    }
  }, [open])

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger disabled={disabled} asChild>
        <Button
          variant="ghost"
          size={'sm'}
          className="flex items-center gap-2 text-base font-medium"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M14 9.33337C13.8232 9.33337 13.6536 9.40361 13.5286 9.52864C13.4036 9.65366 13.3333 9.82323 13.3333 10V12.6667C13.3333 12.8435 13.2631 13.0131 13.1381 13.1381C13.013 13.2631 12.8435 13.3334 12.6666 13.3334H3.33331C3.1565 13.3334 2.98693 13.2631 2.86191 13.1381C2.73688 13.0131 2.66665 12.8435 2.66665 12.6667V10C2.66665 9.82323 2.59641 9.65366 2.47138 9.52864C2.34636 9.40361 2.17679 9.33337 1.99998 9.33337C1.82317 9.33337 1.6536 9.40361 1.52858 9.52864C1.40355 9.65366 1.33331 9.82323 1.33331 10V12.6667C1.33331 13.1971 1.54403 13.7058 1.9191 14.0809C2.29417 14.456 2.80288 14.6667 3.33331 14.6667H12.6666C13.1971 14.6667 13.7058 14.456 14.0809 14.0809C14.4559 13.7058 14.6666 13.1971 14.6666 12.6667V10C14.6666 9.82323 14.5964 9.65366 14.4714 9.52864C14.3464 9.40361 14.1768 9.33337 14 9.33337ZM7.52665 10.4734C7.59005 10.5341 7.66481 10.5816 7.74665 10.6134C7.82645 10.6486 7.91273 10.6669 7.99998 10.6669C8.08723 10.6669 8.17351 10.6486 8.25331 10.6134C8.33515 10.5816 8.40991 10.5341 8.47331 10.4734L11.14 7.80671C11.2655 7.68117 11.336 7.51091 11.336 7.33337C11.336 7.15584 11.2655 6.98558 11.14 6.86004C11.0144 6.73451 10.8442 6.66398 10.6666 6.66398C10.4891 6.66398 10.3188 6.73451 10.1933 6.86004L8.66665 8.39337V2.00004C8.66665 1.82323 8.59641 1.65366 8.47138 1.52864C8.34636 1.40361 8.17679 1.33337 7.99998 1.33337C7.82317 1.33337 7.6536 1.40361 7.52858 1.52864C7.40355 1.65366 7.33331 1.82323 7.33331 2.00004V8.39337L5.80665 6.86004C5.74449 6.79788 5.67069 6.74857 5.58948 6.71493C5.50827 6.68129 5.42122 6.66398 5.33331 6.66398C5.24541 6.66398 5.15836 6.68129 5.07715 6.71493C4.99593 6.74857 4.92214 6.79788 4.85998 6.86004C4.79782 6.9222 4.74851 6.99599 4.71487 7.07721C4.68123 7.15842 4.66392 7.24547 4.66392 7.33337C4.66392 7.42128 4.68123 7.50833 4.71487 7.58954C4.74851 7.67076 4.79782 7.74455 4.85998 7.80671L7.52665 10.4734Z"
              fill="#00AC9C"
            />
          </svg>
          Import
        </Button>
      </DialogTrigger>
      <DialogContent className="pb-14">
        <DialogHeader className="flex items-center justify-between flex-row">
          <DialogTitle>Import Users</DialogTitle>
          <DialogClose>
            <svg
              width="32"
              height="32"
              viewBox="0 0 32 32"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M14.1143 16.0001L7.51465 9.40039L9.40027 7.51477L15.9999 14.1144L22.5996 7.51477L24.4852 9.40039L17.8855 16.0001L24.4852 22.5997L22.5996 24.4853L15.9999 17.8857L9.40027 24.4853L7.51465 22.5997L14.1143 16.0001Z"
                fill="#202224"
              />
            </svg>
          </DialogClose>
        </DialogHeader>
        <div className="border-2 border-dashed max-w-xl mx-auto p-4 md:p-10 flex flex-col items-center space-y-5">
          {errors ? (
            <div className="flex space-x-6">
              <svg
                width="64"
                height="64"
                viewBox="0 0 64 64"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M35.2883 0.06834C42.6978 0.52394 49.6441 4.16874 54.7382 9.18034C60.7584 15.5587 64 23.3039 64 32.4159C64 39.7055 61.2214 46.5395 56.5905 52.4623C51.9596 57.9295 45.4763 62.0299 38.0668 63.3967C30.6574 64.7635 23.2479 63.8523 16.7646 60.2075C10.2813 56.5627 5.18728 51.0955 2.40872 44.2615C-0.36983 37.4275 -0.832922 29.6823 1.48254 22.8483C3.798 15.5587 7.96583 9.63594 14.4491 5.53554C20.4693 1.43514 27.8788 -0.38726 35.2883 0.06834ZM37.6037 58.8407C43.6239 57.4739 49.181 54.2847 53.3489 49.2731C57.0536 44.2615 59.3691 38.3387 58.906 31.9603C58.906 24.6707 56.1274 17.3811 51.0334 12.3695C46.4025 7.81354 40.8454 5.07994 34.3621 4.62434C28.3419 4.16874 21.8586 5.53554 16.7646 9.18034C11.6706 12.8251 7.96583 17.8367 6.11346 24.2151C4.26109 30.1379 4.26109 36.5163 7.03965 42.4391C9.8182 48.3619 13.986 52.9179 19.5431 56.1071C25.1002 59.2963 31.5835 60.2075 37.6037 58.8407ZM32.0466 29.6823L43.1608 18.2923L46.4025 21.4815L35.2883 32.8715L46.4025 44.2615L43.1608 47.4507L32.0466 36.0607L20.9324 47.4507L17.6908 44.2615L28.805 32.8715L17.6908 21.4815L20.9324 18.2923L32.0466 29.6823Z"
                  fill="#EF3826"
                />
              </svg>

              <div>
                <h6 className="text-lg font-semibold">
                  Failed to upload data file
                </h6>
                <p className="text-sm mb-4">{fileName}</p>
                <ErrorMessages errorsInput={errors} />
                <Button
                  onClick={() => {
                    setSuccess(false)
                    setErrors(null)
                    setFileName(null)
                  }}
                  className="font-bold mt-6 rounded-md"
                  variant="window"
                >
                  Select another file
                </Button>
              </div>
            </div>
          ) : success ? (
            <div className="flex space-x-6">
              <svg
                width="64"
                height="64"
                viewBox="0 0 64 64"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M45.6 27.4C45.9644 27.0228 46.1659 26.5176 46.1614 25.9932C46.1568 25.4688 45.9465 24.9672 45.5757 24.5964C45.2049 24.2256 44.7032 24.0153 44.1788 24.0107C43.6545 24.0061 43.1492 24.2077 42.772 24.572L28.172 39.172L21.572 32.572C21.1948 32.2077 20.6896 32.0061 20.1652 32.0107C19.6408 32.0153 19.1392 32.2256 18.7684 32.5964C18.3976 32.9672 18.1872 33.4688 18.1827 33.9932C18.1781 34.5176 18.3797 35.0228 18.744 35.4L26.744 43.4C27.1191 43.775 27.6277 43.9856 28.158 43.9856C28.6884 43.9856 29.197 43.775 29.572 43.4L45.572 27.4H45.6Z"
                  fill="#00AC9C"
                />
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M32 0C14.32 0 0 14.32 0 32C0 49.68 14.32 64 32 64C49.68 64 64 49.68 64 32C64 14.32 49.68 0 32 0ZM4 32C4 16.52 16.52 4 32 4C47.48 4 60 16.52 60 32C60 47.48 47.48 60 32 60C16.52 60 4 47.48 4 32Z"
                  fill="#00AC9C"
                />
              </svg>
              <div>
                <h6 className="text-lg font-semibold">
                  Team Members Imported Successfully
                </h6>
                <p className="text-sm">{fileName}</p>

                <div className="mt-5 flex space-x-4">
                  <Button
                    onClick={() => {
                      setOpen(false)
                      setSuccess(false)
                      setFileName(null)
                    }}
                  >
                    Done
                  </Button>

                  <Button
                    onClick={() => {
                      setSuccess(false)
                      setFileName(null)
                    }}
                    className="font-bold rounded-md"
                    variant="window"
                  >
                    Select another file
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <>
              <p className="text-lg text-center font-semibold">
                Select file to import (XLS, XLSX format, max 2MB).
              </p>
              <div className="!mb-5">
                <Input
                  type="file"
                  accept=".xls,.xlsx"
                  onChange={handleFileChange}
                  className="hidden"
                  id="file-upload"
                />
                <label
                  htmlFor="file-upload"
                  className="cursor-pointer inline-flex text-lg font-bold items-center px-4 border border-transparent rounded-lg py-3 text-white bg-primary focus:outline-none hover:bg-primary/90"
                >
                  {isPending ? 'Uploading...' : 'Upload'}
                </label>
                {fileName && <span className="ml-3">{fileName}</span>}
              </div>

              <p className="text-center">
                Add your team members to this{' '}
                <a
                  href={template}
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary hover:underline"
                >
                  template
                </a>
                , and then
                <br />
                click the Upload button when you are ready to proceed.
              </p>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
